import {assert} from 'Asserts';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigWithMetadata} from '../../redux/flowchartConfigAutosaveManagerCallbacks';
import {getFieldPairKey, getTransitionId} from '../../types/transition/transitionKeyUtils';
import {Transition} from '../../types/transition/TransitionTypes';

export interface TransitionModalResult {
  transition: Transition;
  updatedNodeDataByPTDVId: Record<string, TaskSchemaStaticNodeData>;
}

/**
 * Extracts all task schema node data from the draft state, keyed by PTDV client ID.
 * This captures any node field changes (e.g., added input fields) that occurred in the modal.
 */
export function extractUpdatedNodeDataByPTDVId(
  draftState: FlowchartConfigWithMetadata
): Record<string, TaskSchemaStaticNodeData> {
  const result: Record<string, TaskSchemaStaticNodeData> = {};
  for (const node of draftState.staticFlowchart.nodesStaticData) {
    if (isStaticTaskSchemaNodeData(node)) {
      result[node.taskDefinitionVersionSpec.clientId] = node;
    }
  }
  return result;
}

/**
 * Extracts a Transition from the modal's draft state. The result may be a
 * simple transition (single source → target) or a merging transition (multiple
 * sources → target).
 *
 * When editing an existing transition, the original's id and isPlanned are preserved.
 */
export default function extractTransitionFromDraftState(
  draftState: FlowchartConfigWithMetadata,
  originalTransition?: Transition
): Transition | null {
  const draftTransitions = Object.values(draftState.metadata.transitions);

  let transition: Transition | null;
  if (draftTransitions.length > 0) {
    transition = mergeFromDraftTransitions(draftTransitions, originalTransition);
  } else {
    transition = deriveFromNodesAndEdges(draftState, originalTransition);
  }

  if (transition != null) {
    syncCustomMappingDataWithEdges(
      transition,
      draftState.staticFlowchart.edgesStaticData,
      draftState.staticFlowchart.nodesStaticData
    );
  }

  return transition;
}

/**
 * Syncs customMappingDataByFieldPair with the draft's data flow edges:
 * adds entries for new edges, removes entries whose edges were deleted.
 */
function syncCustomMappingDataWithEdges(
  transition: Transition,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>
): void {
  const sourcePTDVIdSet = new Set(transition.sourcePTDVIds);

  const nodeIdToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeIdToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }

  const updatedCustomMappingDataByFieldPair: Record<string, CustomMappingData | null> = {};

  for (const edge of edges) {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      continue;
    }

    const sourcePTDVId = nodeIdToPTDVId.get(edge.source);
    const targetPTDVId = nodeIdToPTDVId.get(edge.target);
    if (sourcePTDVId == null || targetPTDVId == null) {
      continue;
    }

    if (!sourcePTDVIdSet.has(sourcePTDVId) || targetPTDVId !== transition.targetPTDVId) {
      continue;
    }

    const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

    const fieldPairKey = getFieldPairKey({
      sourcePTDVId,
      sourceSectionType: sourceHandle.sectionType,
      sourceFieldClientId: sourceHandle.fieldClientId,
      targetPTDVId,
      targetSectionType: targetHandle.sectionType,
      targetFieldClientId: targetHandle.fieldClientId,
    });

    updatedCustomMappingDataByFieldPair[fieldPairKey] =
      transition.customMappingDataByFieldPair[fieldPairKey] ?? null;
  }

  transition.customMappingDataByFieldPair = updatedCustomMappingDataByFieldPair;
}

/**
 * The shared editor component considers each control flow edges as a separate transition,
 * because we cannot define a merging transition on the main canvas. (instead, a user must define
 * the merge as an optional transition first and promote it to a planned transition once it exists)
 *
 * On save, the transition modal must reconstitute the "multiple" transitions as a single merge transition
 */
function mergeFromDraftTransitions(
  draftTransitions: Array<Transition>,
  originalTransition?: Transition
): Transition {
  const targetPTDVId = draftTransitions[0].targetPTDVId;

  const allSourcePTDVIds = new Set<string>();
  const mergedTransitionSources: Record<string, TransitionSource> = {};
  const mergedCustomMappingData: Record<string, CustomMappingData | null> = {};

  for (const t of draftTransitions) {
    for (const sourcePTDVId of t.sourcePTDVIds) {
      allSourcePTDVIds.add(sourcePTDVId);
    }
    Object.assign(mergedTransitionSources, t.transitionSourcesBySourcePTDVId);
    Object.assign(mergedCustomMappingData, t.customMappingDataByFieldPair);
  }

  const sourcePTDVIds = [...allSourcePTDVIds];

  return {
    id: originalTransition?.id ?? getTransitionId(sourcePTDVIds, targetPTDVId),
    sourcePTDVIds,
    targetPTDVId,
    transitionSourcesBySourcePTDVId: mergedTransitionSources,
    customMappingDataByFieldPair: mergedCustomMappingData,
    // ADD mode (no original transition) is exclusively for optional transitions.
    // Draft transitions default to isPlanned:true internally, so we must override.
    isPlanned: originalTransition?.isPlanned ?? false,
  };
}

/**
 * Fallback: derive a transition directly from the nodes/edges when the draft
 * metadata has no transitions yet (e.g. user drew edges but transition creation
 * hasn't propagated to metadata).
 */
function deriveFromNodesAndEdges(
  draftState: FlowchartConfigWithMetadata,
  originalTransition?: Transition
): Transition | null {
  const {nodesStaticData, edgesStaticData} = draftState.staticFlowchart;

  const controlFlowEdges = edgesStaticData.filter((e) => e.type === ProcessFlowchartEdgeType.CONTROL_FLOW);
  if (controlFlowEdges.length === 0) {
    return null;
  }

  const nodeIdToPTDVId = new Map<string, string>();
  for (const node of nodesStaticData) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeIdToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }

  const targetNodeIds = new Set(controlFlowEdges.map((e) => e.target));
  const sourceNodeIds = new Set(controlFlowEdges.map((e) => e.source));

  const pureTargetNodeIds = [...targetNodeIds].filter((id) => !sourceNodeIds.has(id));
  assert(pureTargetNodeIds.length <= 1, 'Expected at most one target node for a transition, got multiple', {
    pureTargetNodeIds,
  });
  const targetPTDVId =
    pureTargetNodeIds.length > 0
      ? nodeIdToPTDVId.get(pureTargetNodeIds[0])
      : nodeIdToPTDVId.get([...targetNodeIds][0]);

  if (targetPTDVId == null) {
    return null;
  }

  const sourcePTDVIds = [
    ...new Set(
      controlFlowEdges.map((e) => nodeIdToPTDVId.get(e.source)).filter((id): id is string => id != null)
    ),
  ];

  if (sourcePTDVIds.length === 0) {
    return null;
  }

  return {
    id: originalTransition?.id ?? getTransitionId(sourcePTDVIds, targetPTDVId),
    sourcePTDVIds,
    targetPTDVId,
    transitionSourcesBySourcePTDVId: {...originalTransition?.transitionSourcesBySourcePTDVId},
    customMappingDataByFieldPair: {...originalTransition?.customMappingDataByFieldPair},
    isPlanned: originalTransition?.isPlanned ?? false,
  };
}
